import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, CaretDown, ArrowRight } from "@phosphor-icons/react";
import { track } from "@/lib/analytics";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const SERVICES = [
  { to: "/platform", label: "The Platform" },
  { to: "/services", label: "Care Coordination" },
  { to: "/project-management", label: "Project Management" },
  { to: "/medical-billing-rcm", label: "Medical Billing & RCM" },
];

const NAV = [
  { to: "/programs", label: "Programs" },
];

const RESOURCES = [
  { to: "/insights", label: "Insights (Blog)" },
  { to: "/resources", label: "Resource Library" },
  { to: "/fit", label: "Find Your Fit (quiz)" },
  { to: "/behavioral-health", label: "Behavioral Health (CoCM)" },
  { to: "/compliance", label: "Compliance & Billing" },
  { to: "/implementation", label: "Implementation" },
  { to: "/calculator", label: "Sustainability Planner" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`sticky top-0 z-50 bg-white/80 backdrop-blur-xl transition-shadow duration-300 ${
        scrolled ? "border-b border-slate-200 shadow-[0_1px_0_0_rgba(15,23,42,0.04)]" : "border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <img src="/zoc-mark.png" alt="ZOC Medical" className="h-8 w-auto object-contain" />
          <span className="leading-none">
            <span className="block font-display text-lg font-extrabold text-[#0B2A4E] tracking-tight">ZOC Medical</span>
            <span className="block text-[9px] font-semibold tracking-[0.18em] uppercase text-slate-400 mt-0.5">Platform + Care Services</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-services-trigger"
              className="group inline-flex items-center gap-1 px-3.5 py-2 text-sm font-medium rounded-lg text-slate-600 hover:text-[#0B2A4E] hover:bg-slate-50 transition-colors duration-200 focus:outline-none"
            >
              Services
              <CaretDown size={12} weight="bold" className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 rounded-xl border-slate-200 shadow-lg">
              {SERVICES.map((s) => (
                <DropdownMenuItem key={s.to} asChild>
                  <Link to={s.to} data-testid={`nav-service-${s.to.replace("/", "")}`} className="cursor-pointer rounded-lg py-2.5">
                    {s.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  isActive ? "text-[#0B2A4E] bg-slate-100" : "text-slate-600 hover:text-[#0B2A4E] hover:bg-slate-50"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-resources-trigger"
              className="group inline-flex items-center gap-1 px-3.5 py-2 text-sm font-medium rounded-lg text-slate-600 hover:text-[#0B2A4E] hover:bg-slate-50 transition-colors duration-200 focus:outline-none"
            >
              Resources
              <CaretDown size={12} weight="bold" className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 rounded-xl border-slate-200 shadow-lg">
              {RESOURCES.map((r) => (
                <DropdownMenuItem key={r.to} asChild>
                  <Link to={r.to} data-testid={`nav-resource-${r.to.replace("/", "")}`} className="cursor-pointer rounded-lg py-2.5">
                    {r.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="hidden lg:flex items-center gap-2">
          <Link to="/contact" data-testid="nav-signin" className="px-3.5 py-2 text-sm font-medium text-slate-600 hover:text-[#0B2A4E] transition-colors duration-200">
            Contact
          </Link>
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            onClick={() => track("demo_click", { source: "navbar" })}
            className="group inline-flex items-center gap-1.5 rounded-full bg-[#0B2A4E] text-white pl-5 pr-4 py-2 text-sm font-medium shadow-sm transition-colors duration-200 hover:bg-[#08203B]"
          >
            Book a demo
            <ArrowRight weight="bold" size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden grid place-items-center h-10 w-10 rounded-lg border border-slate-200 text-[#0B2A4E]"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <List size={20} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-slate-200"
          >
            <div className="px-4 sm:px-6 py-5 flex flex-col gap-1">
              <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Services</p>
              {SERVICES.map((item) => (
                <Link key={item.to} to={item.to} data-testid={`mobile-nav-service-${item.to.replace("/", "")}`} className="px-3 py-2.5 rounded-lg text-slate-700 font-medium hover:bg-slate-50">{item.label}</Link>
              ))}
              <div className="h-px bg-slate-100 my-2" />
              {NAV.map((item) => (
                <Link key={item.to} to={item.to} className="px-3 py-2.5 rounded-lg text-slate-700 font-medium hover:bg-slate-50">{item.label}</Link>
              ))}
              <div className="h-px bg-slate-100 my-2" />
              {RESOURCES.map((item) => (
                <Link key={item.to} to={item.to} className="px-3 py-2 rounded-lg text-slate-500 text-sm hover:bg-slate-50">{item.label}</Link>
              ))}
              <Link to="/contact" data-testid="mobile-nav-cta" className="mt-3 inline-flex items-center justify-center rounded-full bg-[#0B2A4E] text-white px-5 py-3 font-medium">
                Book a demo
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
